/**
 * Shared confirm-gate rules for Maestro's approval cards.
 *
 * Purpose: One home for "does this field hold something the user could confirm?",
 *   used by every surface that asks the user to answer for a field — the
 *   MaestroMappingsStepper's Confirm button and ApprovalCard's Approve button
 *   on an mdm_mapping gate, and ApprovalCard's Submit button on a user_input
 *   question (text, date or number). Before ORCH-373 each surface only applied
 *   the rule inside its click handler, so the button looked clickable with
 *   nothing selected and answered the click with a warning instead. Biz Review
 *   found the same defect still on the Budget question's Submit, so the
 *   user_input rule lives here too rather than in the card.
 *
 * Dependencies: none — pure predicates, deliberately free of React so both
 *   components and their tests can call them directly.
 */

/** A plan form value counts as empty when it is nullish, blank text, or an empty array. */
export const isEmptyFieldValue = (v: unknown): boolean => {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string' && v.trim() === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
};

/** The resolved dropdown choice ApprovalMappingField reports for an active gate. */
export interface MappingChoice {
  label: string;
  value: unknown;
}

/**
 * True when an mdm_mapping gate has a selection the user can confirm.
 *
 * Logic: mirrors the resume payload exactly — the choice_value sent to the
 *   orchestrator is the dropdown selection when one was reported, else the plan
 *   form's own value — so the button's enabled state can never disagree with the
 *   click handler's validation. Exactly one value is checked, the same one the
 *   handler would send.
 *
 *   ORCH-376: the plan form is no longer pre-seeded with Maestro's proposal, so
 *   a live gate legitimately has an empty formValue while the dropdown shows the
 *   proposal. Requiring a non-empty formValue on top of the choice would leave
 *   Confirm permanently disabled. ORCH-373's point survives unchanged: a gate
 *   with no choice and no form value is still nothing to confirm, and a reported
 *   choice that is itself empty still blocks Confirm even when the form holds a
 *   value — because that empty choice is what the handler would send.
 * Input: formValue (current plan form value for the gate's fd_key), choice
 *   (ApprovalMappingField's resolved choice; null/undefined when it has none)
 * Output: whether Confirm / Approve should be enabled
 */
export const canConfirmMapping = (
  formValue: unknown,
  choice: MappingChoice | null | undefined
): boolean => {
  const choiceValue = choice?.value !== undefined ? choice.value : formValue;
  return !isEmptyFieldValue(choiceValue);
};

/**
 * True when a user_input question (text, date or number) has an answer to submit.
 *
 * Logic: the same empty-value definition the mapping gates use, applied to the
 *   answer the card would send — the typed text for text/date questions, the
 *   plain number the shared currency renderer handed back for number questions
 *   (null until the user types one). 0 is a real number, so a zero budget can
 *   be submitted; blank or whitespace-only text cannot.
 * Input: answer — the card's current answer for the question
 * Output: whether Submit should be enabled
 */
export const canSubmitUserInput = (answer: unknown): boolean => !isEmptyFieldValue(answer);
